import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Users, Plus, X, Calendar, Map, ChevronRight,
  Clock, CheckCircle2, AlertCircle, Search, Filter,
  Loader2, Phone, MessageSquare, RefreshCw, Trash2,
  BookMarked, UtensilsCrossed
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import '../../assets/css/reservations.css';
import axios from 'axios';
import { baseurl } from '../../baseurl';

/* ═══════════════════════════════════════
   API
═══════════════════════════════════════ */
const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${token}` } });
};

function useToast() {
  const [toast, setToast] = useState({ show: false, msg: '', type: 'success' });
  const fire = useCallback((msg, type = 'success') => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2600);
  }, []);
  return { toast, fire };
}

const TODAY = new Date().toISOString().split('T')[0];

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
function Reservations() {
  const [zones,           setZones]           = useState([]);
  const [tables,          setTables]          = useState([]);
  const [reservations,    setReservations]    = useState([]);
  const [activeZone,      setActiveZone]      = useState('all');
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [showBookingModal,setShowBookingModal] = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [search,          setSearch]          = useState('');
  const [filterDate,      setFilterDate]      = useState(TODAY);

  const [newBooking, setNewBooking] = useState({
    guest: '', phone: '', date: TODAY, time: '19:00', covers: 2, note: '',
  });

  const { toast, fire } = useToast();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [zonesRes, tablesRes, resRes] = await Promise.all([
        api().get('/zones').catch(() => ({ data: { zones: [] } })),
        api().get('/tables').catch(() => ({ data: { tables: [] } })),
        api().get('/reservations').catch(() => ({ data: { reservations: [] } })),
      ]);
      setZones(zonesRes.data.zones || []);
      setTables((tablesRes.data.tables || []).map(t => ({
        ...t, groupId: t.group_id || t.groupId || null,
      })));
      setReservations(resRes.data.reservations || []);
    } catch { fire('Failed to load data', 'error'); }
    finally { setLoading(false); }
  };

  /* ── Only show reserved tables (status === 'reserved') ── */
  const reservedTables = useMemo(() => {
    return tables.filter(t => {
      const isReserved = t.status === 'reserved';
      const inZone     = activeZone === 'all' || String(t.zone_id) === String(activeZone) || t.zone_name === activeZone;
      return isReserved && inZone;
    });
  }, [tables, activeZone]);

  /* ── Filter reservations by date + search ── */
  const filteredReservations = useMemo(() => {
    return reservations.filter(r => {
      const matchDate   = !filterDate || (r.date || '').startsWith(filterDate);
      const matchSearch = !search || r.guest_name?.toLowerCase().includes(search.toLowerCase()) || r.guest?.toLowerCase().includes(search.toLowerCase()) || r.phone?.includes(search);
      return matchDate && matchSearch;
    });
  }, [reservations, filterDate, search]);

  const selectedTable = useMemo(() => tables.find(t => t.id === selectedTableId) ?? null, [selectedTableId, tables]);

  /* Reservations for selected table (on the chosen date) */
  const tableReservations = useMemo(() =>
    filteredReservations.filter(r => String(r.table_id || r.tableId) === String(selectedTableId)),
    [filteredReservations, selectedTableId]
  );

  /* Stats */
  const stats = useMemo(() => ({
    total:     filteredReservations.length,
    confirmed: filteredReservations.filter(r => r.status === 'confirmed').length,
    arrived:   filteredReservations.filter(r => r.status === 'arrived' || r.status === 'seated').length,
    cancelled: filteredReservations.filter(r => r.status === 'cancelled').length,
  }), [filteredReservations]);

  /* ── Add reservation ── */
  const handleAddReservation = async () => {
    if (!newBooking.guest.trim()) { fire('Guest name is required', 'error'); return; }
    if (!selectedTableId) { fire('No table selected', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        table_id:   selectedTableId,
        guest_name: newBooking.guest,
        phone:      newBooking.phone,
        date:       newBooking.date,
        time:       newBooking.time,
        covers:     parseInt(newBooking.covers),
        note:       newBooking.note,
        status:     'confirmed',
      };
      const r = await api().post('/reservations', payload);
      setReservations(prev => [...prev, r.data.reservation || { ...payload, id: Date.now() }]);
      // Mark table as reserved in local state
      setTables(prev => prev.map(t => t.id === selectedTableId ? { ...t, status: 'reserved' } : t));
      setShowBookingModal(false);
      setNewBooking({ guest: '', phone: '', date: TODAY, time: '19:00', covers: 2, note: '' });
      fire('Reservation created ✓');
    } catch (err) {
      // Optimistic fallback
      const fallback = { id: Date.now(), table_id: selectedTableId, guest_name: newBooking.guest, phone: newBooking.phone, date: newBooking.date, time: newBooking.time, covers: parseInt(newBooking.covers), note: newBooking.note, status: 'confirmed' };
      setReservations(prev => [...prev, fallback]);
      setTables(prev => prev.map(t => t.id === selectedTableId ? { ...t, status: 'reserved' } : t));
      setShowBookingModal(false);
      setNewBooking({ guest: '', phone: '', date: TODAY, time: '19:00', covers: 2, note: '' });
      fire('Saved locally (server error)');
    } finally { setSaving(false); }
  };

  /* ── Mark arrived ── */
  const handleMarkArrived = async (res) => {
    try {
      await api().put(`/reservations/${res.id}`, { status: 'arrived' });
      setReservations(prev => prev.map(r => r.id === res.id ? { ...r, status: 'arrived' } : r));
      setTables(prev => prev.map(t => String(t.id) === String(res.table_id || res.tableId) ? { ...t, status: 'occupied' } : t));
      fire('Guest marked as arrived ✓');
    } catch {
      setReservations(prev => prev.map(r => r.id === res.id ? { ...r, status: 'arrived' } : r));
      fire('Marked arrived');
    }
  };

  /* ── Cancel reservation ── */
  const handleCancelReservation = async (res) => {
    try {
      await api().put(`/reservations/${res.id}`, { status: 'cancelled' });
    } catch {}
    setReservations(prev => prev.map(r => r.id === res.id ? { ...r, status: 'cancelled' } : r));
    // Free table if no other active reservations for it
    const others = reservations.filter(r => r.id !== res.id && String(r.table_id || r.tableId) === String(res.table_id || res.tableId) && r.status === 'confirmed');
    if (!others.length) {
      try { await api().put(`/tables/${res.table_id || res.tableId}/status`, { status: 'available' }); } catch {}
      setTables(prev => prev.map(t => String(t.id) === String(res.table_id || res.tableId) ? { ...t, status: 'available' } : t));
    }
    fire('Reservation cancelled');
  };

  /* ── Delete reservation ── */
  const handleDeleteReservation = async (res) => {
    try { await api().delete(`/reservations/${res.id}`); } catch {}
    setReservations(prev => prev.filter(r => r.id !== res.id));
    fire('Reservation deleted');
  };

  const statusColor = s => ({
    confirmed: { color:'#ff8c00', bg:'rgba(255,140,0,0.10)' },
    arrived:   { color:'#22c55e', bg:'rgba(34,197,94,0.10)'  },
    seated:    { color:'#22c55e', bg:'rgba(34,197,94,0.10)'  },
    cancelled: { color:'#ef4444', bg:'rgba(239,68,68,0.10)'  },
    no_show:   { color:'#9ca3af', bg:'rgba(156,163,175,0.10)' },
  }[s] || { color:'#6366f1', bg:'rgba(99,102,241,0.10)' });

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  if (loading) {
    return (
      <div className="rw-reservation-container">
        <Navbar />
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flex:1, gap:10, color:'var(--muted)', minHeight:'60vh' }}>
          <Loader2 size={28} className="tw-spin"/> <span style={{ fontWeight:600 }}>Loading reservations…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rw-reservation-container">
      <Navbar />

      {/* Toolbar */}
      <div className="res-toolbar">
        <div className="toolbar-left">
          <h2 className="res-page-title">Reservations</h2>
          <div className="res-stats-row">
            <span className="res-stat-pill total">{stats.total} Total</span>
            <span className="res-stat-pill confirmed">{stats.confirmed} Confirmed</span>
            <span className="res-stat-pill arrived">{stats.arrived} Arrived</span>
            {stats.cancelled > 0 && <span className="res-stat-pill cancelled">{stats.cancelled} Cancelled</span>}
          </div>
        </div>
        <div className="toolbar-actions">
          <div className="res-search-wrap">
            <Search size={13}/>
            <input placeholder="Search guest…" value={search} onChange={e => setSearch(e.target.value)}/>
            {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', cursor:'pointer', display:'flex', padding:0 }}><X size={10}/></button>}
          </div>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
            style={{ border:'1px solid rgba(0,0,0,0.12)', borderRadius:8, padding:'5px 10px', fontSize:12, fontWeight:600, outline:'none', color:'var(--ink)', background:'#fff' }}/>
          <button className="res-refresh-btn" onClick={loadData}><RefreshCw size={14}/></button>
        </div>
      </div>

      <div className="pos-main-layout">

        {/* Zone Sidebar */}
        <aside className="zone-sidebar">
          <div className="sidebar-label">SECTIONS</div>
          <div className="zone-list">
            <button className={`zone-btn ${activeZone === 'all' ? 'active' : ''}`} onClick={() => { setActiveZone('all'); setSelectedTableId(null); }}>
              <Map size={15}/><span>All Zones</span><ChevronRight size={13} style={{ marginLeft:'auto' }}/>
            </button>
            {zones.map(z => (
              <button key={z.id} className={`zone-btn ${String(activeZone) === String(z.id) || activeZone === z.name ? 'active' : ''}`}
                onClick={() => { setActiveZone(z.id); setSelectedTableId(null); }}>
                <UtensilsCrossed size={13}/><span>{z.name}</span><ChevronRight size={13} style={{ marginLeft:'auto' }}/>
              </button>
            ))}
          </div>
          <div className="status-legend">
            <div className="legend-item"><span className="dot reserved"/><span>Reserved</span></div>
            <div className="legend-item"><span className="dot available" style={{ background:'#22c55e' }}/><span>Available</span></div>
          </div>
        </aside>

        {/* Center: Reserved Tables Only */}
        <section className="floorplan-canvas">
          <div className="canvas-header">
            <div>
              <h3>Reserved Tables</h3>
              <p style={{ margin:0, fontSize:12, color:'var(--muted)' }}>
                {reservedTables.length === 0
                  ? 'No reserved tables in this zone'
                  : `${reservedTables.length} table${reservedTables.length > 1 ? 's' : ''} reserved · click to manage`}
              </p>
            </div>
            <button onClick={() => { setSelectedTableId(null); setShowBookingModal(true); }}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 14px', borderRadius:8, border:'none', background:'var(--tomato, #ff4d2e)', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>
              <Plus size={13}/> New Reservation
            </button>
          </div>

          {reservedTables.length === 0 ? (
            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--muted)', padding:40 }}>
              <BookMarked size={52} strokeWidth={1} style={{ opacity:0.3 }}/>
              <p style={{ fontWeight:700, fontSize:15, margin:0 }}>No reserved tables</p>
              <p style={{ fontSize:12, margin:0, textAlign:'center' }}>All tables are currently available in this zone</p>
              <button onClick={() => setShowBookingModal(true)}
                style={{ marginTop:4, display:'flex', alignItems:'center', gap:5, padding:'8px 18px', borderRadius:8, border:'none', background:'var(--tomato, #ff4d2e)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                <Plus size={13}/> Create Reservation
              </button>
            </div>
          ) : (
            <div className="reserved-table-grid">
              {reservedTables.map(table => {
                const tableRes = filteredReservations.filter(r => String(r.table_id || r.tableId) === String(table.id));
                const upcoming = tableRes.find(r => r.status === 'confirmed');
                const isSelected = selectedTableId === table.id;
                return (
                  <div key={table.id} className={`reserved-table-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedTableId(isSelected ? null : table.id)}>
                    <div className="rtc-header">
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span className="rtc-table-label">{table.label || table.id}</span>
                        {table.zone_name && <span style={{ fontSize:9, color:'var(--muted)', fontWeight:600 }}>{table.zone_name}</span>}
                      </div>
                      <span style={{ fontSize:10, color:'var(--muted)', display:'flex', alignItems:'center', gap:3 }}>
                        <Users size={9}/> {table.capacity}
                      </span>
                    </div>
                    {upcoming ? (
                      <div className="rtc-body">
                        <div className="rtc-guest-name">{upcoming.guest_name || upcoming.guest}</div>
                        <div className="rtc-meta">
                          <span><Clock size={9}/> {upcoming.time}</span>
                          <span><Users size={9}/> {upcoming.covers} guests</span>
                        </div>
                        {upcoming.phone && <div style={{ fontSize:9, color:'var(--muted)', display:'flex', alignItems:'center', gap:3, marginTop:3 }}><Phone size={8}/> {upcoming.phone}</div>}
                        <div style={{ marginTop:6 }}>
                          <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:100, background: statusColor(upcoming.status).bg, color: statusColor(upcoming.status).color }}>
                            {upcoming.status?.charAt(0).toUpperCase() + upcoming.status?.slice(1)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize:11, color:'var(--muted)', padding:'6px 0', display:'flex', alignItems:'center', gap:4 }}>
                        <AlertCircle size={11}/> No confirmed booking
                      </div>
                    )}
                    <div className="rtc-footer">
                      <span style={{ fontSize:9, color:'var(--muted)' }}>{tableRes.length} reservation{tableRes.length !== 1 ? 's' : ''}</span>
                      <ChevronRight size={12} style={{ color:'var(--muted)', transform: isSelected ? 'rotate(90deg)' : 'none', transition:'0.2s' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Right Sidebar: Reservation Details */}
        <aside className={`booking-sidebar ${selectedTable ? 'open' : ''}`}>
          {selectedTable ? (
            <div className="sidebar-content">
              <div className="sb-header">
                <div>
                  <h4 style={{ fontWeight:800, margin:0, fontSize:14 }}>Table {selectedTable.label || selectedTable.id}</h4>
                  <small style={{ color:'var(--muted)', fontSize:11 }}>{selectedTable.capacity} Seats{selectedTable.zone_name ? ` · ${selectedTable.zone_name}` : ''}</small>
                </div>
                <button onClick={() => setSelectedTableId(null)} style={{ border:'none', background:'rgba(0,0,0,0.06)', borderRadius:8, padding:6, cursor:'pointer', display:'flex' }}><X size={16}/></button>
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'10px 0 6px' }}>
                <span style={{ fontSize:11, fontWeight:700, color:'var(--muted)' }}>Reservations</span>
                <button onClick={() => setShowBookingModal(true)}
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:6, border:'none', background:'rgba(255,77,46,0.09)', color:'var(--tomato, #ff4d2e)', fontSize:10, fontWeight:700, cursor:'pointer' }}>
                  <Plus size={10}/> Add
                </button>
              </div>

              {tableReservations.length === 0 ? (
                <div className="empty-booking-state">
                  <AlertCircle size={36} strokeWidth={1}/>
                  <h5>No Reservations</h5>
                  <p>No bookings for this table on {formatDate(filterDate)}.</p>
                  <button className="btn-primary" style={{ width:'100%' }} onClick={() => setShowBookingModal(true)}>
                    <Plus size={14}/> Create Reservation
                  </button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {tableReservations.map(res => (
                    <ReservationCard key={res.id} res={res} statusColor={statusColor}
                      onArrived={() => handleMarkArrived(res)}
                      onCancel={() => handleCancelReservation(res)}
                      onDelete={() => handleDeleteReservation(res)}/>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="sidebar-empty">
              <Calendar size={40} strokeWidth={1}/>
              <p>Select a reserved table to manage bookings</p>
            </div>
          )}
        </aside>
      </div>

      {/* ═══ Booking Modal ═══ */}
      {showBookingModal && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="booking-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin:0, fontSize:14, fontWeight:800 }}>
                New Reservation{selectedTableId ? ` · Table ${selectedTable?.label || selectedTableId}` : ''}
              </h3>
              <button onClick={() => setShowBookingModal(false)} style={{ border:'none', background:'rgba(0,0,0,0.06)', borderRadius:6, padding:5, cursor:'pointer', display:'flex' }}><X size={14}/></button>
            </div>
            <div className="modal-body">

              {/* Table selector if none selected */}
              {!selectedTableId && (
                <div className="input-group-res">
                  <label>Table</label>
                  <select onChange={e => setSelectedTableId(Number(e.target.value))} defaultValue=""
                    style={{ width:'100%', border:'1px solid rgba(0,0,0,0.12)', borderRadius:8, padding:'8px 10px', fontSize:12, outline:'none' }}>
                    <option value="" disabled>Select a table</option>
                    {tables.filter(t => t.is_active !== false).map(t => (
                      <option key={t.id} value={t.id}>Table {t.label || t.id} — {t.capacity} seats ({t.status})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="input-group-res">
                <label>Guest Name *</label>
                <input type="text" placeholder="Enter full name" value={newBooking.guest}
                  onChange={e => setNewBooking({ ...newBooking, guest: e.target.value })} autoFocus/>
              </div>
              <div className="input-group-res">
                <label>Phone</label>
                <input type="tel" placeholder="+91 99999 00000" value={newBooking.phone}
                  onChange={e => setNewBooking({ ...newBooking, phone: e.target.value })}/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="input-group-res">
                  <label>Date</label>
                  <input type="date" value={newBooking.date} min={TODAY}
                    onChange={e => setNewBooking({ ...newBooking, date: e.target.value })}/>
                </div>
                <div className="input-group-res">
                  <label>Time</label>
                  <input type="time" value={newBooking.time}
                    onChange={e => setNewBooking({ ...newBooking, time: e.target.value })}/>
                </div>
              </div>
              <div className="input-group-res">
                <label>Party Size</label>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={() => setNewBooking(b => ({ ...b, covers: Math.max(1, b.covers - 1) }))}
                    style={{ width:30, height:30, borderRadius:6, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:16 }}>−</button>
                  <span style={{ fontSize:16, fontWeight:800, minWidth:24, textAlign:'center' }}>{newBooking.covers}</span>
                  <button onClick={() => setNewBooking(b => ({ ...b, covers: b.covers + 1 }))}
                    style={{ width:30, height:30, borderRadius:6, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:16 }}>+</button>
                </div>
              </div>
              <div className="input-group-res">
                <label>Note</label>
                <textarea placeholder="e.g. Window seat, birthday, allergies…" value={newBooking.note}
                  onChange={e => setNewBooking({ ...newBooking, note: e.target.value })} rows={2}
                  style={{ width:'100%', border:'1px solid rgba(0,0,0,0.12)', borderRadius:8, padding:'8px 10px', fontSize:12, outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBookingModal(false)}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'var(--muted)' }}>
                Cancel
              </button>
              <button onClick={handleAddReservation} disabled={saving || !newBooking.guest.trim()}
                style={{ padding:'8px 20px', borderRadius:8, border:'none', background:'var(--tomato, #ff4d2e)', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:5, opacity: saving ? 0.7 : 1 }}>
                {saving ? <Loader2 size={12} className="tw-spin"/> : <CheckCircle2 size={12}/>} Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <div className={`tw-toast ${toast.show ? 'show' : ''}`} style={{ position:'fixed', bottom:20, left:'50%', transform:'translateX(-50%)', background:'#111', color:'#fff', padding:'8px 16px', borderRadius:10, fontSize:12, fontWeight:700, zIndex:9999, display:'flex', alignItems:'center', gap:6, transition:'opacity 0.3s', opacity: toast.show ? 1 : 0, pointerEvents:'none' }}>
        <CheckCircle2 size={13}/> {toast.msg}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   RESERVATION CARD
═══════════════════════════════════════ */
function ReservationCard({ res, statusColor, onArrived, onCancel, onDelete }) {
  const sc      = statusColor(res.status);
  const gName   = res.guest_name || res.guest || '—';
  const isCancelled = res.status === 'cancelled';
  const isArrived   = res.status === 'arrived' || res.status === 'seated';

  return (
    <div style={{ background:'#fff', borderRadius:10, border:'1px solid rgba(0,0,0,0.08)', padding:'10px 12px', display:'flex', flexDirection:'column', gap:7 }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:800, color:'var(--ink)' }}>{gName}</div>
          {res.phone && <div style={{ fontSize:10, color:'var(--muted)', display:'flex', alignItems:'center', gap:3, marginTop:1 }}><Phone size={9}/> {res.phone}</div>}
        </div>
        <span style={{ fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:100, background:sc.bg, color:sc.color, flexShrink:0 }}>
          {res.status?.charAt(0).toUpperCase() + res.status?.slice(1)}
        </span>
      </div>

      <div style={{ display:'flex', gap:10, fontSize:11, color:'var(--muted)' }}>
        <span style={{ display:'flex', alignItems:'center', gap:3 }}><Clock size={10}/> {res.time}</span>
        <span style={{ display:'flex', alignItems:'center', gap:3 }}><Users size={10}/> {res.covers} guests</span>
        {res.date && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Calendar size={10}/> {formatDate(res.date)}</span>}
      </div>

      {res.note && <div style={{ fontSize:10, color:'var(--muted)', background:'rgba(0,0,0,0.03)', borderRadius:6, padding:'4px 7px', display:'flex', alignItems:'center', gap:4 }}><MessageSquare size={9}/> {res.note}</div>}

      {!isCancelled && (
        <div style={{ display:'flex', gap:6, marginTop:2 }}>
          {!isArrived && (
            <button onClick={onArrived}
              style={{ flex:1, padding:'6px', borderRadius:7, border:'none', background:'rgba(34,197,94,0.12)', color:'#16a34a', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
              <CheckCircle2 size={11}/> Arrived
            </button>
          )}
          <button onClick={onCancel}
            style={{ flex:1, padding:'6px', borderRadius:7, border:'none', background:'rgba(239,68,68,0.09)', color:'#ef4444', fontSize:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
            <X size={11}/> Cancel
          </button>
          <button onClick={onDelete}
            style={{ padding:'6px 8px', borderRadius:7, border:'1px solid rgba(0,0,0,0.08)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', color:'var(--muted)' }}>
            <Trash2 size={11}/>
          </button>
        </div>
      )}
      {isCancelled && (
        <button onClick={onDelete}
          style={{ width:'100%', padding:'5px', borderRadius:7, border:'1px solid rgba(0,0,0,0.08)', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4, fontSize:10, fontWeight:700, color:'var(--muted)' }}>
          <Trash2 size={10}/> Remove
        </button>
      )}
    </div>
  );
}

export default Reservations;
